/*
 * Copyright (C) 2025 JPMorgan Chase & Company
 * All rights reserved.
 *
 * Redistribution and use in source and binary forms, with or without
 * modification, is not permitted.
 */

import {ExdsGrid, ExdsLayout, ExdsSpacing, ExdsText} from "@exds/core";
import React, {useContext, useEffect, useState} from "react";
import {ExdsRadio, ExdsRadioGroup} from "@exds/controls";
import {ExdsIconLogoAndroid, ExdsIconLogoApple} from "@exds/icons";
import './DeviceSelection.scss';
import ButtonGroup from "../../ButtonGroup/ButtonGroup";
import {ExdsAlert} from "@exds/feedback";
import AppContext from "../../../AppContext";
import {RADIO_BUTTONS_ERROR_TEXT, STEP_SKIPPED} from "../../../constants/constants";
import Alert from "../../Alert/Alert";
import {TuxLoadingIndicator} from "@tuxedo/react-component-library";
import {addPageLoadEvent} from "../../AdobeAnalytics/AdobeAnalytics";

export default function DeviceSelection(props) {
  const { setActiveMobileAppSetupPage, handleModalAction, skipError, setSkipError, setPageName, isSoftTokenEnabledBool} = props;
  const { mobileDeviceContext, loadAA, sidContext} = useContext(AppContext);
  const [showError, setShowError] = useState(false);
  const [mobileDevice, setMobileDevice] = mobileDeviceContext;
  const sid = sidContext.at(0);
  const pageName = 'Mobile App Setup | Device Selection';

  useEffect(() => {
    if(loadAA) {
      setPageName(pageName);
      addPageLoadEvent(pageName, sid);
    }
  }, [loadAA, sid]); // eslint-disable-line react-hooks/exhaustive-deps

  const inputsValid = () => {
    return mobileDevice !== null;
  };

  const handleContinue = () => {
    if(inputsValid()) {
      setShowError(false);
      if(isSoftTokenEnabledBool){
        setActiveMobileAppSetupPage('rsaInfo');
      } else {
        setActiveMobileAppSetupPage('smartLogOn');
      }
    } else {
      setShowError(true);
    }
    setSkipError({ showError: false, correlationId: '', isLoading: false });
  };

  const handleRadioChange = (selectedRadio) => {
    if(selectedRadio.id.includes('iOS')) {
      setMobileDevice('IPHONE');
    } else {
      setMobileDevice('ANDROID');
    }
    setShowError(false);
  };

  const modalDetails = {
    title: 'Are you sure you want to skip Mobile App Setup?',
    modalActionButton: 'Yes, I want to skip',
    continueButton: 'No, let\'s continue'
  };

  return (
    <>
      {skipError?.isLoading && <div className="loading-spinner"><TuxLoadingIndicator/></div>}
      {!skipError?.isLoading &&
        <ExdsGrid columnSpan={8} className='step-details'>
          { skipError.showError && <Alert correlationId={skipError.correlationId} loadAA={loadAA} pageName={pageName} sid={sid}/> }
          <ExdsText primitive="div">
            At JPMC, we're committed to making your experience both seamless and secure with our mobile apps. The
            following screens will guide you through the installation and setup of our apps, designed to simplify your access to our
            corporate network.
            <br/>
            <br/>
            <b>Applications should only be installed on your own personal mobile device.</b>
          </ExdsText>
          <div className={showError? 'device-selection-error':'device-selection'}>
            <p className='section-header'>Device Selection</p>
            <ExdsRadioGroup
              layout="horizontal"
              text="Select your device’s operating system"
              onChangeCommit={(selectedRadio) => handleRadioChange(selectedRadio)}
            >
              <ExdsLayout
                alignContent="flex-end"
                alignItems="stretch"
                display="flex"
                flexDirection="row"
                flexWrap="nowrap"
                gap="16px"
                justifyContent="flex-start"
              >
                <div className={mobileDevice && mobileDevice==='IPHONE'? 'radio-container-select':'radio-container'}>
                  <ExdsRadio data-testid='ios' checked={mobileDevice === 'IPHONE'} id='iOS'>
                    <div className='radio-text'>
                      <ExdsText>iOS (Apple)</ExdsText>
                      <ExdsIconLogoApple aria-hidden color="regular" size="extraLarge"/>
                    </div>
                  </ExdsRadio>
                </div>

                <div className={mobileDevice && mobileDevice==='ANDROID'? 'radio-container-select':'radio-container'}>
                  <ExdsRadio data-testid='android' checked={mobileDevice === 'ANDROID'} id='Android'>
                    <div className='radio-text'>
                      <ExdsText>Android</ExdsText>
                      <ExdsIconLogoAndroid aria-hidden color="regular" size="extraLarge"/>
                    </div>
                  </ExdsRadio>
                </div>
              </ExdsLayout>
            </ExdsRadioGroup>
            {showError && <ExdsSpacing margin={{top: '4px'}}>
              <ExdsAlert
                accessibleText="defaultVisibleText"
                fullWidth
                size="extraSmall"
                text={RADIO_BUTTONS_ERROR_TEXT}
                variant="error"
              />
            </ExdsSpacing>}
            <ButtonGroup
              advanceButtonContent="Continue"
              advanceButtonAction={handleContinue}
              modalButtonContent="Skip Mobile App Setup"
              modalButtonType="link"
              modalDetails={modalDetails}
              modalAction={ () => handleModalAction('SKIP_ALL', STEP_SKIPPED, STEP_SKIPPED, false)}
            />
          </div>
        </ExdsGrid>
      }
    </>
  );
}

